import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { QueryParameters } from "@lifeat/shared/system/RouteConstants";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtBodyPrimary } from "@common/LifeAtText/LifeAtText";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { auth } from "@firebase-local/firebase";
import useQueryParam from "@hooks/useQueryParam";
import InfoPageTemplate from "@pages/templates/InfoPageTemplate";
import { Size } from "@themes/Device";
import Styles from "@themes/Styles";
import { lightTheme } from "@themes/Theme";
import { sendUserVerificationEmail } from "@user/service/UserService";
import UserAccountModal from "../../../user/components/UserAccountModal/UserAccountModal";
import ChevronLeftIcon from "/resources/icons/ChevronLeftArrow.svg";

const VerifyRoot = styled(InfoPageTemplate)`
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-width: 400px;
    max-width: 100%;
`;

const AuthBlockWrapper = styled.div`
    display: flex;
    justify-content: center;

    @media (max-width: ${Size.tablet}) {
        width: 100%;
    }
`;

const AuthBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
    fill: ${lightTheme.iconColor};
`;

function DesktopAppMainLanding() {
    const query = useQueryParam();

    const { user }: UserContextType = useUserContext();

    const successRedirectUrl = query.has(QueryParameters.RedirectSuccess)
        ? decodeURIComponent(query.get(QueryParameters.RedirectSuccess) ?? "")
        : undefined;

    if (user && !user.emailVerified) {
        return <PleaseVerifyEmailBlock />;
    }
    return (
        <VerifyRoot forceLightMode showLogo>
            <AuthBlockWrapper>
                <AuthBlock>
                    {!user && (
                        <UserAccountModal
                            forceOpenModal={true}
                            contentsOnly={true}
                            onSuccessRedirectUrl={successRedirectUrl}
                        />
                    )}
                    {user && !user.emailVerified && <PleaseVerifyEmailBlock />}
                </AuthBlock>
            </AuthBlockWrapper>
        </VerifyRoot>
    );
}

export default DesktopAppMainLanding;

const BackButton = styled(LifeAtButtonV2)`
    align-self: flex-start;
`;

const VerifyBlockContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: center;

    margin-top: 32px;

    text-align: center;
`;

const VerifyTopCluster = styled.div`
    display: flex;
    flex-direction: column;
`;

const VerifyTitle = styled.p`
    font-weight: bold;
`;

const VerifySubtitle = styled.p``;

const VerifyTroubleshootCluster = styled.div`
    display: flex;
    flex-direction: column;
`;

const VerifyButtonCluster = styled.div`
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
`;

function PleaseVerifyEmailBlock() {
    const { user, setUser, setUserData }: UserContextType = useUserContext();

    const history = useHistory();
    const query = useQueryParam();

    const verificationEmailRedir = query.has(QueryParameters.RedirectSuccess)
        ? decodeURIComponent(query.get(QueryParameters.RedirectSuccess) ?? "")
        : undefined;

    useEffect(() => {
        if (user.emailVerified) {
            history.go(0);
        }
    }, [user]);

    return (
        user && (
            <VerifyRoot forceLightMode showLogo>
                <BackButton
                    variant={"ghost"}
                    onClick={() => {
                        auth.signOut().then(() => {
                            setUser(null);
                            setUserData(null);
                        });
                    }}
                >
                    <StyledChevronLeftIcon />
                    Return to login
                </BackButton>

                <VerifyBlockContainer>
                    <VerifyTopCluster>
                        <VerifyTitle>Please verify your email</VerifyTitle>
                        <VerifySubtitle>We sent an email to {user.email}</VerifySubtitle>
                    </VerifyTopCluster>
                    <VerifyTroubleshootCluster>
                        <LifeAtBodyPrimary>Not getting the email?</LifeAtBodyPrimary>
                        <LifeAtBodyPrimary>
                            Check your spam or make sure you added the right email above.
                        </LifeAtBodyPrimary>
                    </VerifyTroubleshootCluster>
                    <VerifyButtonCluster>
                        <LifeAtButtonV2
                            variant="outline"
                            fullWidth
                            onClick={() => sendUserVerificationEmail(user, verificationEmailRedir)}
                        >
                            Resend Email
                        </LifeAtButtonV2>
                        <LifeAtButtonV2
                            overrideTextColor={"#FFF"}
                            overrideBackgroundColor={Styles.buttonFillBlue}
                            disableHover={true}
                            fullWidth
                            onClick={() => history.go(0)}
                        >
                            Reload LifeAt
                        </LifeAtButtonV2>
                    </VerifyButtonCluster>
                </VerifyBlockContainer>
            </VerifyRoot>
        )
    );
}
